/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ContentBodyProps, OverflowWrapperProps, PopoverPresentationProps } from "@insite/mobius/Popover";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { get, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { styledBox } from "@monsma/XDL/Components/Common/Box";
import { screen } from "@monsma/XDL/Helpers";
import Folder from "@monsma/XDL/Icons/Folder";
import Trash from "@monsma/XDL/Icons/Trash";
import Color from "color";
import { css } from "styled-components";

export const zoneTitle: TypographyPresentationProps = {
    weight: "normal",
    size: 16,
    color: "primary.contrast",
    css: css`
        padding: 2px 5px;
        background-color: ${getColor("text.main")};
        margin: 0 6px -7px 6px;
        border: 1px solid ${getColor("common.ui13")};
        font-size: 9px;
        border-radius: 3px 3px 0 0;
        height: 20px;
        display: flex;
        align-items: center;
    `,
};

export const popover: PopoverPresentationProps = {
    xPosition: "end",
};

export const popoverWrapper: OverflowWrapperProps = {
    as: "div",
    _height: "auto",
};

export const popoverContentBody: ContentBodyProps = {
    as: "div",
    _height: "100%",
};

export const labelStyle = css`
    font-size: 13px;
    color: ${getColor("text.main")};
    margin-right: 5px;
    flex: 0 0 auto;
    font-weight: 400;
    line-height: 22px;

    &:after {
        content: ":";
    }
`;

export const valueStyle = css`
    font-weight: 600;
    letter-spacing: 0.5px;
    color: ${getColor("common.ui10")};
    font-size: 13px;
    line-height: 22px;
`;

export const warehouseWrapper = css`
    margin-top: 10px;
    ${styledBox};
    margin-bottom: 0;
`;

export const warehouseNameStyle: TypographyPresentationProps = {
    variant: "p",
    weight: 600,
    css: css`
        font-size: 14px;
        line-height: 20px;
        margin-bottom: 10px;
    `,
};

export const hideScrollBar = css`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    /* this prevent scroll outside the container */
    overscroll-behavior-y: contain;
`;

// Header title and link styles for widgets EX: Recent orders, Recent Quotes, ...
export const widgetHeaderContentStyle = css`
    width: 100%;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;

    & > ${TypographyStyle} {
        margin-bottom: 10px;
    }
`;

export const styledScrollbar = css`
    ::-webkit-scrollbar {
        width: 8px;
    }

    ::-webkit-scrollbar-track {
        background: ${getColor("common.ui18")};
    }

    ::-webkit-scrollbar-thumb {
        background: ${getColor("common.ui19")};
        border-radius: 2px;
        border: 2px solid transparent;
        background-clip: content-box;
    }
`;

export const imageHoverEffect = css`
    &:after {
        content: " ";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: ${({ theme }) => Color(get(theme, "colors.common.black")).fade(0.85).toString()};
        opacity: 0;
        transition: all 0.2s linear;
    }
`;

export const sharedProductAddToListLinkStyles: ProductAddToListLinkStyles = {
    link: {
        icon: {
            iconProps: {
                src: Folder,
                size: 12,
                color: "common.ui5",
            },
            position: "left",
        },
    },
};

export const sharedSelectListStyles = {
    wrapper: {
        css: css`
            display: inline-flex;
            align-items: center;
        `,
    },
    selectList: {
        labelProps: {
            size: 13,
            lineHeight: "22px",
            weight: "normal",
            ellipsis: false,
            css: css`
                width: auto;
                padding: 0;
                margin-right: 13px;
                flex: 0 0 auto;

                ${screen("lt", "sm")} {
                    width: 55px;
                    text-align: left;
                }
            `,
        },
        cssOverrides: {
            formField: css`
                align-items: center;
            `,
            formInputWrapper: css`
                width: 172px;

                ${screen("lte", "sm")} {
                    flex: 1;
                    width: 100%;
                }
            `,
        },
        sizeVariant: "small",
    },
    iconSymbol: {
        size: 18,
        color: "common.ui5",
        css: css`
            margin-right: 10px;
            align-items: center;
            display: flex;
        `,
    },
};

export const removeLinkStyle = {
    icon: {
        iconProps: {
            src: Trash,
            color: "common.ui17",
            size: 13,
            css: css`
                margin-right: 5px;
            `,
        },
    },
    color: "danger",
    css: css`
        ${getProp("theme.link.defaultProps.css")};
        &.active,
        &:hover {
            ${TypographyStyle} {
                color: ${getColor("common.ui16")};
            }
        }
    `,
};

export const companyLogoItem: any = {
    css: css`
        display: none;
        padding: 0;
        width: 125px;
        float: left; // for print only

        @media print {
            display: block;
            padding-bottom: 20px;
            padding-left: 25px;
            margin-top: 5px;
        }

        img {
            display: block;
            max-width: 100%;
            height: auto;
        }
    `,
};
